import { service } from "./services";
import { Settings } from "./Settings";
import { CreateElement, Scale } from "./MyHelpers";
import { ZIndex } from "./ZIndex";

const OVERLAY_ID = "backend-connection-lost-modal";
const BLOCKED_EVENTS: (keyof WindowEventMap)[] = [
	"pointerdown",
	"pointerup",
	"mousedown",
	"mouseup",
	"click",
	"touchstart",
	"touchend",
	"touchmove",
	"wheel",
	"keydown",
	"keyup",
	"contextmenu",
];

let overlay: HTMLElement = null;
let titleElement: HTMLElement = null;
let messageElement: HTMLElement = null;
let retryButton: HTMLButtonElement = null;
let visible = false;
let hasConnectedOnce = false;
let listenersInstalled = false;
let retryHandler: (() => Promise<void> | void) | null = null;

function ShouldLockOnConnectionLost(): boolean {
	return Settings.FailOnConnectionLost !== false;
}

function CurrentTexts(): { title: string; message: string } {
	const t = service.translator?.t;
	return {
		title: t?.connectionLostTitle ?? "CONEXIÓN PERDIDA",
		message: t?.connectionLostMessage ?? "Se perdió la conexión con el servidor. No es posible continuar hasta que se restablezca.",
	};
}

function BlockInteraction(event: Event): void {
	if (!visible) {
		return;
	}

	// The retry button is the sole control intentionally available while the
	// game is locked. Everything else remains blocked behind the modal.
	if ((event.target as Element | null)?.closest?.("[data-backend-connection-retry]")) {
		return;
	}

	event.preventDefault();
	event.stopPropagation();
	event.stopImmediatePropagation();
}

function EnsureListeners(): void {
	if (listenersInstalled) {
		return;
	}

	listenersInstalled = true;

	for (const eventName of BLOCKED_EVENTS) {
		window.addEventListener(eventName, BlockInteraction, { capture: true, passive: false });
	}

	window.addEventListener("offline", () => {
		if (hasConnectedOnce) {
			NotifyBackendConnectionLost();
		}
	});
}

function EnsureOverlay(): HTMLElement {
	EnsureListeners();

	if (overlay) {
		return overlay;
	}

	overlay = CreateElement("div", {
		id: OVERLAY_ID,
		classes: ["backend-connection-lost-modal"],
		style: {
			position: "fixed",
			top: "0px",
			left: "0px",
			width: "100%",
			height: "100%",
			backgroundColor: "rgba(16, 16, 20, 0.74)",
			color: "white",
			display: "none",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			zIndex: `${ZIndex.ConnectionLost}`,
			pointerEvents: "auto",
			userSelect: "none",
			touchAction: "none",
		},
	});

	const dialog = CreateElement("div", {
		classes: ["backend-connection-lost-dialog"],
		parentToAppend: overlay,
		style: {
			width: "min(80%, 900px)",
			backgroundColor: "black",
			color: "white",
			borderRadius: Scale.XYpx(40),
			border: `${Scale.XYpx(4)} solid gold`,
			padding: `${Scale.Hpx(50)} ${Scale.Wpx(60)}`,
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			textAlign: "center",
			gap: Scale.Hpx(28),
			boxSizing: "border-box",
		},
	});

	titleElement = CreateElement("div", {
		classes: ["backend-connection-lost-title"],
		parentToAppend: dialog,
		style: {
			fontSize: Scale.fhpx(64),
			lineHeight: "1.1",
			fontWeight: "700",
			letterSpacing: "0.04em",
		},
	});

	messageElement = CreateElement("div", {
		classes: ["backend-connection-lost-message"],
		parentToAppend: dialog,
		style: {
			fontSize: Scale.fhpx(36),
			lineHeight: "1.3",
			maxWidth: "18em",
		},
	});

	retryButton = CreateElement("button", {
		attrs: { type: "button", "data-backend-connection-retry": "true" },
		parentToAppend: dialog,
		innerHTML: "TRY AGAIN",
		style: {
			minWidth: Scale.Wpx(260),
			padding: `${Scale.Hpx(18)} ${Scale.Wpx(42)}`,
			border: `${Scale.XYpx(3)} solid #f4cd00`,
			borderRadius: Scale.XYpx(14),
			backgroundColor: "#24242a",
			color: "#ffffff",
			fontSize: Scale.fhpx(30),
			fontWeight: "700",
			letterSpacing: "0.04em",
			cursor: "pointer",
		},
	}) as HTMLButtonElement;

	retryButton.addEventListener("click", async (event) => {
		event.preventDefault();
		event.stopPropagation();
		if (retryHandler == null || retryButton.disabled) {
			return;
		}

		retryButton.disabled = true;
		retryButton.textContent = "RETRYING...";
		try {
			await retryHandler();
		}
		catch {
			// The regular reconnect loop remains active if a manual attempt fails.
		}
		finally {
			if (visible) {
				retryButton.disabled = false;
				retryButton.textContent = "TRY AGAIN";
			}
		}
	});

	document.body.appendChild(overlay);
	return overlay;
}

function RefreshTexts(): void {
	const texts = CurrentTexts();
	if (titleElement) {
		titleElement.textContent = texts.title;
	}
	if (messageElement) {
		messageElement.textContent = texts.message;
	}
}

export function MarkBackendConnected(): void {
	hasConnectedOnce = true;
	EnsureListeners();
}

/** Installs the app-specific reconnect action used by the modal's retry button. */
export function SetBackendConnectionRetryHandler(handler: (() => Promise<void> | void) | null): void {
	retryHandler = handler;
}

export function IsBackendConnectionLost(): boolean {
	return visible;
}

export function NotifyBackendConnectionLost(): void {
	if (!ShouldLockOnConnectionLost() || !hasConnectedOnce) {
		return;
	}

	EnsureOverlay();
	RefreshTexts();

	visible = true;
	overlay.style.display = "flex";
	service.robotPlay?.Disable();
}

export function NotifyBackendConnectionRestored(): void {
	hasConnectedOnce = true;
	EnsureListeners();
	visible = false;

	if (overlay) {
		overlay.style.display = "none";
	}
	if (retryButton) {
		retryButton.disabled = false;
		retryButton.textContent = "TRY AGAIN";
	}
}
